import { Component } from '@theme/component';

/**
 * Client logo wall (sections/client-logos.liquid, grid mode): every few seconds one cell
 * flips over to reveal another client, so the wall looks like it's always updating.
 *
 * - The next logo comes from the hidden pool (clients beyond the visible count); the one it
 *   replaces goes to the back of the pool. With an empty pool, two cells trade logos.
 * - Timing is jittered (0.6x to 1.4x the interval), and the last few cells flipped are skipped,
 *   so it reads as organic rather than a loop.
 * - Runs only while the wall is on screen and the tab is visible, and never under reduced
 *   motion.
 * - Sequence mode (`data-cells="5, 8, 11, 16"`, the designers' version): only those cells flip,
 *   in the order 1st, 3rd, 2nd, 4th, one every 1.4s with a 2.6s rest after each round. Each one
 *   turns over to its paired pool logo (the Nth pool entry for the Nth cell) and back again.
 *
 * @typedef {{ grid: HTMLElement, cells: HTMLElement[], pool?: HTMLTemplateElement[] }} ClientLogosRefs
 * @extends {Component<ClientLogosRefs>}
 */
class ClientLogosComponent extends Component {
  requiredRefs = ['grid'];

  /** @type {string[]} Pool of logo markup waiting to be shown. */
  #pool = [];

  /** @type {number[]} Recently flipped cells, skipped for a while. */
  #recent = [];

  /** @type {number | undefined} */
  #timer;

  #visible = false;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if ((this.refs.cells?.length ?? 0) < 2) return;

    this.#pool = this.#shuffle((this.refs.pool ?? []).map((template) => template.innerHTML));
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      this.#schedule();
    });
    this.#observer.observe(this);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    window.clearTimeout(this.#timer);
  }

  #onVisibility = () => this.#schedule();

  /** @type {number[]} Sequence mode: zero-based cell indexes. */
  get #cells() {
    return (this.dataset.cells ?? '')
      .split(',')
      .map((n) => Number(n.trim()) - 1)
      .filter((n) => Number.isInteger(n) && n >= 0 && n < (this.refs.cells?.length ?? 0));
  }

  #step = 0;

  /** @type {Map<number, string>} Sequence mode: each cell's alternate logo. */
  #alternates = new Map();

  #schedule() {
    window.clearTimeout(this.#timer);
    if (!this.#visible || document.hidden) return;
    if (this.#cells.length) {
      const delay = this.#step > 0 && this.#step % this.#cells.length === 0 ? 2600 : 1400;
      this.#timer = window.setTimeout(() => {
        this.#flipSequence();
        this.#schedule();
      }, this.#step === 0 ? 500 : delay);
      return;
    }
    const base = (Number(this.dataset.interval) || 2.5) * 1000;
    const delay = base * (0.6 + Math.random() * 0.8);
    this.#timer = window.setTimeout(() => {
      this.#flipOne();
      this.#schedule();
    }, delay);
  }

  #flipSequence() {
    const cells = this.#cells;
    const order = [0, 2, 1, 3].filter((i) => i < cells.length);
    for (let i = 4; i < cells.length; i++) order.push(i);
    const slot = order[this.#step % order.length] ?? 0;
    this.#step++;
    const index = cells[slot];
    if (index === undefined) return;
    const cell = this.refs.cells?.[index];
    if (!cell) return;
    if (!this.#alternates.has(index)) {
      const template = this.refs.pool?.[slot];
      if (!template) return;
      this.#alternates.set(index, template.innerHTML);
    }
    const covered = this.#flip(cell, this.#alternates.get(index) ?? '');
    this.#alternates.set(index, covered);
  }

  #flipOne() {
    const cells = this.refs.cells ?? [];
    const index = this.#pickCell(cells.length);
    const cell = cells[index];
    if (!cell) return;

    if (this.#pool.length) {
      const incoming = this.#pool.shift() ?? '';
      const outgoing = this.#flip(cell, incoming);
      if (outgoing) this.#pool.push(outgoing);
    } else {
      // Nothing waiting: trade logos with another cell, the second flip a beat later.
      const otherIndex = this.#pickCell(cells.length, index);
      const other = cells[otherIndex];
      if (!other) return;
      const mine = this.#front(cell)?.innerHTML ?? '';
      const theirs = this.#front(other)?.innerHTML ?? '';
      this.#flip(cell, theirs);
      window.setTimeout(() => this.#flip(other, mine), 350);
    }
  }

  /**
   * Puts `markup` on the hidden face and turns the card over. Returns the markup it covered.
   * @param {HTMLElement} cell
   * @param {string} markup
   */
  #flip(cell, markup) {
    const card = cell.querySelector('.client-logos__flip');
    if (!(card instanceof HTMLElement)) return '';
    const front = this.#front(cell);
    const back = this.#back(cell);
    if (!front || !back) return '';

    const covered = front.innerHTML;
    back.innerHTML = markup;
    back.querySelectorAll('img').forEach((img) => (img.loading = 'eager'));
    card.toggleAttribute('data-flipped');
    // Roles swap; the faces keep their classes (and so their rotations).
    front.removeAttribute('data-showing');
    front.setAttribute('aria-hidden', 'true');
    back.setAttribute('data-showing', '');
    back.removeAttribute('aria-hidden');
    return covered;
  }

  /** @param {HTMLElement} cell */
  #front(cell) {
    return cell.querySelector('.client-logos__face[data-showing]');
  }

  /** @param {HTMLElement} cell */
  #back(cell) {
    return cell.querySelector('.client-logos__face:not([data-showing])');
  }

  /**
   * A random cell that hasn't flipped recently.
   * @param {number} count
   * @param {number} [exclude]
   */
  #pickCell(count, exclude = -1) {
    const avoid = new Set([...this.#recent, exclude]);
    const options = Array.from({ length: count }, (_, i) => i).filter((i) => !avoid.has(i));
    const choices = options.length ? options : Array.from({ length: count }, (_, i) => i);
    const index = choices[Math.floor(Math.random() * choices.length)] ?? 0;
    this.#recent.push(index);
    if (this.#recent.length > Math.min(5, Math.floor(count / 2))) this.#recent.shift();
    return index;
  }

  /**
   * @template T
   * @param {T[]} list
   * @returns {T[]}
   */
  #shuffle(list) {
    const copy = [...list];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }
}

if (!customElements.get('client-logos-component')) {
  customElements.define('client-logos-component', ClientLogosComponent);
}
